import { AlertCircle } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { useSegmentCounts } from "../api/useSegmentCounts";
import { SEGMENT_LIMITS, SEGMENT_TRANSITION } from "../constants";
import { GlobalCombineBlock } from "./GlobalCombineBlock";
import { LimitAddButton } from "./LimitAddButton";
import { LogicDivider } from "./LogicDivider";
import { OverlapNotice } from "./OverlapNotice";
import { OverridesBlock } from "./OverridesBlock";
import { RuleBlock } from "./RuleBlock";
import { RuleSummary } from "./RuleSummary";
import type { BuilderDraft } from "../hooks/useBuilderDraft";
import { useOverlapMerge } from "../hooks/useOverlapMerge";
import { useRestoreOrder } from "../hooks/useRestoreOrder";
import { useUndoToast } from "../hooks/useUndoToast";
import { createEmptyRule } from "../mappers";
import type { SegmentFormValues } from "../schemas";

import { Alert, AlertDescription } from "@/components/ui/v2/alert";
import type { TourDemoControl } from "@/features/segments/tour/constants";
import type { SegmentField } from "@/features/segments/types";
import { useTour } from "@/features/tours/useTour";
import useToast from "@/lib/hooks/useToast";

type BuilderTabProps = {
  readOnly: boolean;
  clientId?: string;
  fields: SegmentField[];
  counts: ReturnType<typeof useSegmentCounts>;
  draft: BuilderDraft;
  activeConditionKey: string | null;
  onActiveConditionChange: (key: string | null) => void;
  tourDemoControl: TourDemoControl | null;
};

const ADD_RULE_SELECTOR = ":scope > button:last-child";

type FocusRequest = { ruleId?: string; conditionKey?: string };

const getFocusSelector = ({ ruleId, conditionKey }: FocusRequest) => {
  if (ruleId) return `[data-rule-id="${ruleId}"] :is(button, [role="radio"])`;
  if (conditionKey) return `[data-condition-key="${conditionKey}"]`;
  return ADD_RULE_SELECTOR;
};

export function BuilderTab({
  readOnly,
  clientId,
  fields,
  counts,
  draft,
  activeConditionKey,
  onActiveConditionChange,
  tourDemoControl,
}: BuilderTabProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const showUndoToast = useUndoToast();
  const restoreOrder = useRestoreOrder();
  const { isRunning: isTourRunning } = useTour();
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);
  const [highlightedGroupKey, setHighlightedGroupKey] = useState<
    string | null
  >(null);
  const {
    control,
    getValues,
    watch,
    formState: { errors },
  } = useFormContext<SegmentFormValues>();
  const {
    fields: ruleFields,
    append,
    insert,
    remove,
  } = useFieldArray({ control, name: "rules" });
  const type = watch("type");
  const overlap = useOverlapMerge({
    fields,
    enabled: !readOnly && !isTourRunning,
    removeRules: remove,
    insertRule: insert,
    restoreOrder,
  });

  useEffect(() => {
    if (!focusRequest) return;
    containerRef.current
      ?.querySelector<HTMLElement>(getFocusSelector(focusRequest))
      ?.focus();
    setFocusRequest(null);
  }, [focusRequest]);

  const handleAddRule = () => {
    const rule = createEmptyRule();
    append(rule);
    onActiveConditionChange(rule.conditions[0]?.clientKey ?? null);
  };

  const focusFirstSurvivor = ([survivor]: string[]) => {
    setHighlightedGroupKey(null);
    onActiveConditionChange(null);
    setFocusRequest({ conditionKey: survivor });
  };

  const handleFixAll = () => focusFirstSurvivor(overlap.mergeAll());

  const handleMergeGroup = (key: string) =>
    focusFirstSurvivor(overlap.mergeGroups([key]));

  const handleRemoveRule = (index: number) => {
    const removed = getValues(`rules.${index}`);
    const countBefore = ruleFields.length;
    const nextRuleId = ruleFields[index + 1]?.id;
    restoreOrder.track(getValues("rules").map((rule) => rule.clientKey));
    onActiveConditionChange(null);
    remove(index);
    setFocusRequest({ ruleId: nextRuleId });
    showUndoToast({
      message: t("admin.segments.builder.ruleRemoved"),
      description: <RuleSummary rule={removed} fields={fields} />,
      onUndo: () => {
        const current = getValues("rules");
        if (current.length >= Math.max(SEGMENT_LIMITS.rules, countBefore)) {
          showToast(
            t("admin.segments.builder.limit.rules", {
              count: SEGMENT_LIMITS.rules,
            }),
            "warning",
          );
          return;
        }
        const currentKeys = current.map((rule) => rule.clientKey);
        insert(restoreOrder.indexFor(removed.clientKey, currentKeys), removed);
      },
    });
  };

  return (
    <div ref={containerRef} className="flex flex-col gap-4 py-6">
      <AnimatePresence initial={false}>
        {overlap.groups.length > 0 && (
          <OverlapNotice
            groups={overlap.groups}
            fields={fields}
            sourcesOf={(group) =>
              overlap.sourcesOf(group).map(({ ruleKey, condition }) => ({
                condition,
                ruleIndex: ruleFields.findIndex(
                  (ruleField) => ruleField.clientKey === ruleKey,
                ),
              }))
            }
            onFixAll={handleFixAll}
          />
        )}
      </AnimatePresence>

      <GlobalCombineBlock readOnly={readOnly} />

      {ruleFields.length === 0 && readOnly && (
        <p className="text-sm text-muted-foreground">
          {t("admin.segments.builder.noRules")}
        </p>
      )}

      <AnimatePresence initial={false}>
        {ruleFields.map((field, index) => (
          <motion.div
            key={field.id}
            data-rule-id={field.id}
            layout="position"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={SEGMENT_TRANSITION}
            className="flex flex-col gap-4"
          >
            {index > 0 && <LogicDivider logic={type} />}
            <RuleBlock
              ruleIndex={index}
              readOnly={readOnly}
              clientId={clientId}
              fields={fields}
              count={counts.ruleCounts[index] ?? { isFetching: false }}
              activeConditionKey={activeConditionKey}
              onActiveConditionChange={onActiveConditionChange}
              overlap={{
                groups: overlap.groupsByRule.get(field.clientKey) ?? [],
                byCondition: overlap.overlapByCondition,
                highlightedGroupKey,
                sourcesOf: overlap.sourcesOf,
                onMerge: handleMergeGroup,
                onHighlight: setHighlightedGroupKey,
              }}
              tourDemoControl={tourDemoControl}
              onRemove={() => handleRemoveRule(index)}
              editedHint={draft.editedHints.get(index)}
            />
          </motion.div>
        ))}
      </AnimatePresence>

      {errors.rules?.message && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>{errors.rules.message}</AlertDescription>
        </Alert>
      )}

      {!readOnly && (
        <LimitAddButton
          label={t("admin.segments.builder.addRule")}
          limitMessage={t("admin.segments.builder.limit.rules", {
            count: SEGMENT_LIMITS.rules,
          })}
          isAtLimit={ruleFields.length >= SEGMENT_LIMITS.rules}
          onAdd={handleAddRule}
          className="w-full"
        />
      )}

      <OverridesBlock
        readOnly={readOnly}
        overrides={draft.overrides}
        changeSummary={draft.changeSummary}
        onCountsChange={draft.listCounts.adjust}
      />
    </div>
  );
}
